import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Select,
  Skeleton,
  TextArea,
  TextInput,
  UntrustedContent,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { prettyJson, toneForSideEffect } from '../lib/format';

interface Endpoint {
  endpointId: string;
  method: string;
  path: string;
  summary: string | null;
  description: string | null;
  deprecated: boolean;
  proposedOperationKey: string;
  proposedToolName: string | null;
  suggestedSideEffect: string;
  suggestedIdempotent: boolean;
  inputSchema: unknown;
  outputSchema: unknown;
  notes: string[];
}

interface AnalyzeResult {
  knowledgeSourceId: string;
  knowledgeSourceVersionId: string;
  document: {
    title: string;
    version: string | null;
    openapi: string;
    servers: string[];
    checksum: string;
    byteSize: number;
    warnings: string[];
  };
  endpoints: Endpoint[];
  diff: {
    added: string[];
    removed: string[];
    changed: Array<{ endpointId: string; fields: string[] }>;
  } | null;
  injectionMarkers: string[];
  notice: string;
}

export function OpenApiImportPage() {
  const services = useResource<{ items: Array<{ serviceKey: string; name: string }> }>(
    '/api/admin/services',
  );
  const accounts = useResource<{
    items: Array<{ id: string; providerKey: string; label: string; environment: string }>;
  }>('/api/admin/provider-accounts');
  const toast = useToast();

  const [serviceKey, setServiceKey] = useState('');
  const [mode, setMode] = useState<'inline' | 'url'>('inline');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [accountId, setAccountId] = useState('');
  const [environment, setEnvironment] = useState('test');
  const [busy, setBusy] = useState(false);
  const [inspecting, setInspecting] = useState<Endpoint | null>(null);

  const analyze = async () => {
    setBusy(true);
    try {
      const result = await api.post<AnalyzeResult>('/api/admin/catalog/openapi/analyze', {
        serviceKey,
        source: mode === 'inline' ? { type: 'inline', content } : { type: 'url', url },
      });
      setAnalysis(result);
      setSelected(new Set());
      toast.success(
        'Dokument przeanalizowany',
        `${result.endpoints.length} endpointów do przeglądu`,
      );
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    } finally {
      setBusy(false);
    }
  };

  const createDrafts = async () => {
    if (!analysis) return;
    setBusy(true);
    try {
      const result = await api.post<{ created: Array<{ operationKey: string }>; notice: string }>(
        '/api/admin/catalog/openapi/create-drafts',
        {
          serviceKey,
          knowledgeSourceVersionId: analysis.knowledgeSourceVersionId,
          providerAccountId: accountId,
          environment,
          endpointIds: [...selected],
        },
      );
      toast.success(`Utworzono ${result.created.length} wersji roboczych`, result.notice);
      setSelected(new Set());
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Import OpenAPI"
        description="Import tworzy wyłącznie wersje robocze, ukryte przed agentami. Publikacja jest osobną, świadomą decyzją."
      />

      <Card title="1. Wskaż dokument">
        <div className="ah-grid cols-2">
          <Field label="Usługa docelowa">
            {(id) => (
              <Select
                id={id}
                value={serviceKey}
                onChange={(event) => setServiceKey(event.target.value)}
              >
                <option value="">— wybierz —</option>
                {(services.data?.items ?? []).map((service) => (
                  <option key={service.serviceKey} value={service.serviceKey}>
                    {service.serviceKey} — {service.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Źródło">
            {(id) => (
              <Select
                id={id}
                value={mode}
                onChange={(event) => setMode(event.target.value as 'inline' | 'url')}
              >
                <option value="inline">Wklejona treść</option>
                <option value="url">Adres URL (HTTPS, publiczny)</option>
              </Select>
            )}
          </Field>
        </div>
        {mode === 'inline' ? (
          <Field label="Dokument OpenAPI 3.0 / 3.1 (JSON lub YAML)">
            {(id) => (
              <TextArea
                id={id}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                rows={10}
              />
            )}
          </Field>
        ) : (
          <Field
            label="Adres URL"
            hint="Pobranie przechodzi przez tę samą ochronę SSRF co każde wywołanie upstream."
          >
            {(id) => (
              <TextInput
                id={id}
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://example.com/openapi.json"
              />
            )}
          </Field>
        )}
        <Button
          variant="primary"
          disabled={busy || !serviceKey || (mode === 'inline' ? !content : !url)}
          onClick={analyze}
        >
          {busy ? 'Analizuję…' : 'Analizuj dokument'}
        </Button>
      </Card>

      {analysis && (
        <>
          <Card title="2. Przegląd dokumentu">
            <Callout>{analysis.notice}</Callout>
            {analysis.injectionMarkers.length > 0 && (
              <Callout tone="danger">
                Wykryto wzorce charakterystyczne dla prompt injection:{' '}
                {analysis.injectionMarkers.join(', ')}. Dokument jest traktowany jako dane
                niezaufane. Przejrzyj opisy przed publikacją.
              </Callout>
            )}
            {analysis.document.warnings.map((warning) => (
              <Callout tone="warn" key={warning}>
                {warning}
              </Callout>
            ))}
            <dl className="ah-kv">
              <dt>Tytuł</dt>
              <dd>{analysis.document.title}</dd>
              <dt>Wersja dokumentu</dt>
              <dd>{analysis.document.version ?? '—'}</dd>
              <dt>OpenAPI</dt>
              <dd>{analysis.document.openapi}</dd>
              <dt>Serwery</dt>
              <dd className="ah-mono">{analysis.document.servers.join(', ') || '—'}</dd>
              <dt>Suma kontrolna</dt>
              <dd className="ah-mono">{analysis.document.checksum.slice(0, 24)}…</dd>
            </dl>

            {analysis.diff && (
              <>
                <h3 style={{ fontSize: 13, marginTop: 16 }}>Różnice wobec poprzedniego importu</h3>
                <div className="ah-grid cols-3">
                  <div>
                    <Badge tone="ok">dodane: {analysis.diff.added.length}</Badge>
                    <ul className="ah-mono" style={{ fontSize: 12 }}>
                      {analysis.diff.added.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <Badge tone="danger">usunięte: {analysis.diff.removed.length}</Badge>
                    <ul className="ah-mono" style={{ fontSize: 12 }}>
                      {analysis.diff.removed.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <Badge tone="warn">zmienione: {analysis.diff.changed.length}</Badge>
                    <ul className="ah-mono" style={{ fontSize: 12 }}>
                      {analysis.diff.changed.map((item) => (
                        <li key={item.endpointId}>
                          {item.endpointId} ({item.fields.join(', ')})
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </>
            )}
          </Card>

          <Card
            title="3. Wybierz endpointy"
            description="Zaznacz tylko te, które są rzeczywiście potrzebne."
            tight
          >
            <DataTable
              rows={analysis.endpoints}
              rowKey={(row) => row.endpointId}
              empty={<EmptyState title="Dokument nie zawiera obsługiwanych endpointów" />}
              columns={[
                {
                  key: 'select',
                  header: '',
                  render: (row) => (
                    <input
                      type="checkbox"
                      aria-label={`Wybierz ${row.endpointId}`}
                      checked={selected.has(row.endpointId)}
                      onChange={(event) => {
                        const next = new Set(selected);
                        if (event.target.checked) next.add(row.endpointId);
                        else next.delete(row.endpointId);
                        setSelected(next);
                      }}
                    />
                  ),
                },
                {
                  key: 'endpoint',
                  header: 'Endpoint',
                  render: (row) => (
                    <span className="ah-mono">
                      {row.method} {row.path}
                    </span>
                  ),
                },
                {
                  key: 'key',
                  header: 'Proponowany klucz',
                  render: (row) => (
                    <>
                      <div className="ah-mono">{row.proposedOperationKey}</div>
                      {row.proposedToolName && (
                        <div
                          style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}
                          className="ah-mono"
                        >
                          MCP: {row.proposedToolName}
                        </div>
                      )}
                    </>
                  ),
                },
                {
                  key: 'side',
                  header: 'Sugerowane skutki',
                  render: (row) => (
                    <>
                      <Badge tone={toneForSideEffect(row.suggestedSideEffect)}>
                        {row.suggestedSideEffect}
                      </Badge>
                      {row.notes.length > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--ah-warn)', marginTop: 3 }}>
                          {row.notes.length} uwag
                        </div>
                      )}
                    </>
                  ),
                },
                {
                  key: 'actions',
                  header: '',
                  align: 'right',
                  render: (row) => (
                    <Button
                      size="sm"
                      onClick={() =>
                        setInspecting(inspecting?.endpointId === row.endpointId ? null : row)
                      }
                    >
                      {inspecting?.endpointId === row.endpointId ? 'Ukryj' : 'Zobacz'}
                    </Button>
                  ),
                },
              ]}
            />

            {inspecting && (
              <div className="ah-card-body">
                <h3 style={{ fontSize: 13 }}>{inspecting.endpointId}</h3>
                {inspecting.description && (
                  <UntrustedContent source={`dokument OpenAPI: ${analysis.document.title}`}>
                    <pre className="ah-code">{inspecting.description}</pre>
                  </UntrustedContent>
                )}
                {inspecting.notes.length > 0 && (
                  <Callout tone="warn">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {inspecting.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </Callout>
                )}
                <div className="ah-grid cols-2">
                  <div>
                    <h4 style={{ fontSize: 12, color: 'var(--ah-text-muted)' }}>
                      Schemat wejściowy
                    </h4>
                    <pre className="ah-code">{prettyJson(inspecting.inputSchema)}</pre>
                  </div>
                  <div>
                    <h4 style={{ fontSize: 12, color: 'var(--ah-text-muted)' }}>
                      Schemat wyjściowy
                    </h4>
                    <pre className="ah-code">{prettyJson(inspecting.outputSchema)}</pre>
                  </div>
                </div>
              </div>
            )}
          </Card>

          <Card title="4. Utwórz wersje robocze">
            <div className="ah-grid cols-2">
              <Field label="Konto dostawcy" hint="Cel routingu tworzony razem z operacją.">
                {(id) => (
                  <Select
                    id={id}
                    value={accountId}
                    onChange={(event) => setAccountId(event.target.value)}
                  >
                    <option value="">— wybierz —</option>
                    {(accounts.data?.items ?? []).map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.providerKey}/{account.label} ({account.environment})
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Środowisko">
                {(id) => (
                  <Select
                    id={id}
                    value={environment}
                    onChange={(event) => setEnvironment(event.target.value)}
                  >
                    <option value="test">test</option>
                    <option value="staging">staging</option>
                    <option value="production">production</option>
                  </Select>
                )}
              </Field>
            </div>
            <Callout tone="warn">
              Powstaną operacje w stanie <strong>DRAFT</strong> i z ekspozycją MCP{' '}
              <strong>HIDDEN</strong>. Zanim opublikujesz, uzupełnij opis agentowy, potwierdź
              klasyfikację skutków ubocznych i przetestuj operację.
            </Callout>
            <Button
              variant="primary"
              disabled={busy || selected.size === 0 || !accountId}
              onClick={createDrafts}
            >
              Utwórz {selected.size} wersji roboczych
            </Button>
          </Card>
        </>
      )}

      {services.loading && <Skeleton rows={3} />}
    </>
  );
}
